import { LEGACY_FILID_TO_GALLERI_ID } from './legacyFilId';

/**
 * Sentralt galleri hentes fra DiBKs eksterne galleri-API. Lese-endepunktene er
 * åpne uten nøkkel og har CORS, så appene kaller dem direkte.
 */
export const GALLERI_API_BASE = 'https://dibk-drommeplan.azurewebsites.net/api/eksternt/galleri';

/** Speiler `EksterntGallerielementDTO` i DiBKs API. */
export interface GalleriElement {
    id: number;
    tittel: string | null;
    beskrivelse: string | null;
    kommunenummer: string | null;
    prioritet: number;
    opprettet: string | null;
    oppdatert: string | null;
    mimeType: string | null;
    /** Direkte-URL hos DiBK. Brukes kun som signal for om elementet har bilde. */
    bildeUrl: string | null;
}

export class GalleriApiError extends Error {
    readonly status: number;

    constructor(status: number) {
        super(`Galleri-API svarte ${status}`);
        this.name = 'GalleriApiError';
        this.status = status;
    }
}

/**
 * Henter alle galleri-elementer hos DiBK, også kommunale, sortert på
 * prioritet. Lagrede rader kan peke på kommunale elementer (DiBKs import satte
 * `GallerielementId` også for dem), så oppslag på id må bruke hele lista.
 */
export async function fetchGalleri(signal?: AbortSignal): Promise<GalleriElement[]> {
    const res = await fetch(GALLERI_API_BASE, { signal, headers: { Accept: 'application/json' } });
    if (!res.ok) throw new GalleriApiError(res.status);
    const items = (await res.json()) as GalleriElement[];
    return items.sort((a, b) => a.prioritet - b.prioritet);
}

/** Elementene som kan velges i admin: de sentrale, altså uten kommunenummer. */
export function sentraleElementer(items: readonly GalleriElement[]): GalleriElement[] {
    return items.filter(item => item.kommunenummer == null);
}

/**
 * Bilde-URL hos DiBK. `oppdatert` versjonerer adressen: DiBK lar bildene
 * caches i et døgn, så et byttet bilde får ny URL og hentes på nytt.
 */
export function galleriBildeUrl(id: number, oppdatert?: string | null): string {
    const url = `${GALLERI_API_BASE}/${id}/bilde`;
    return oppdatert ? `${url}?v=${encodeURIComponent(oppdatert)}` : url;
}

/**
 * Oversetter en gammel DiBK-fil-id (`Seksjonselementer.FilId`, lagret før
 * #13788) til galleri-id via frosset tabell. Tallområdene overlapper (gammel
 * 104 «Byggegrense», ny 104 «Hensynssone»), så `FilId` tolkes aldri som
 * galleri-id direkte.
 */
export function parseFilIdRef(filId: string | number | null | undefined): number | null {
    if (filId == null || filId === '') return null;
    const n = Number(filId);
    return Number.isInteger(n) ? LEGACY_FILID_TO_GALLERI_ID[n] ?? null : null;
}

/** Referanse til sentralt galleri slik den ligger i et lagret seksjonselement. */
export interface GalleriReferanse {
    /** `GallerielementId` — galleri-id hos DiBK (nye og migrerte rader). */
    galleriElementId?: number | null;
    /** `FilId` — DiBKs gamle fil-id (rader lagret før #13788, ikke migrert). */
    filId?: string | number | null;
}

/** Galleri-id for et lagret element, eller `null` hvis det ikke kommer fra sentralt galleri. */
export function galleriIdFor(ref: GalleriReferanse): number | null {
    if (ref.galleriElementId != null) return ref.galleriElementId;
    return parseFilIdRef(ref.filId);
}

/**
 * Raden peker på DiBK — via `GallerielementId` eller en numerisk fil-id —
 * også når DiBKs API ikke (lenger) kjenner elementet.
 */
export function harDibkReferanse(ref: GalleriReferanse): boolean {
    if (ref.galleriElementId != null) return true;
    if (ref.filId == null || ref.filId === '') return false;
    const n = Number(ref.filId);
    return Number.isInteger(n) && n > 0;
}

/** Bilde for et eget element: bare en absolutt URL i `FilId` gir et bilde. */
function egenBildeUrl(filId: string | number | null | undefined): string {
    const str = filId == null ? '' : String(filId);
    return str.startsWith('http') ? str : '';
}

/** DiBKs galleri (sentrale og kommunale elementer) slått opp på id. */
export type SentraltGalleriOppslag = ReadonlyMap<number, GalleriElement>;

export function lagSentraltGalleriOppslag(elementer: readonly GalleriElement[]): SentraltGalleriOppslag {
    return new Map(elementer.map(el => [el.id, el]));
}

/** Sentralt galleri slik appene ser det: lastes, hentet, eller feilet. */
export type SentraltGalleriTilstand =
    | { status: 'laster' }
    | { status: 'feil' }
    | { status: 'ok'; oppslag: SentraltGalleriOppslag };

/**
 * - `ok`: data fra DiBK (eller et ikke-sentralt element med egen tekst).
 * - `laster`: sentralt galleri er ikke hentet ennå.
 * - `utilgjengelig`: API-et feilet, eller DiBK kjenner ikke id-en.
 */
export type ElementVisningStatus = 'ok' | 'laster' | 'utilgjengelig';

export interface ElementVisning {
    tittel: string;
    beskrivelse: string | null;
    imageUrl: string;
    /** Elementet kommer fra sentralt galleri — DiBK eier innholdet, og det kan ikke redigeres. */
    sentralt: boolean;
    status: ElementVisningStatus;
}

/**
 * Bestemmer tittel, beskrivelse og bilde for et lagret (ikke-kommunalt)
 * seksjonselement.
 *
 * Elementer fra sentralt galleri lagres kun som referanse (`GallerielementId`),
 * og alt innhold hentes fra DiBK. Eventuell tekst i AGOL-raden (fra før
 * #13788) brukes aldri — DiBK er eneste kilde. Øvrige elementer bruker lagret
 * tekst.
 */
export function resolveElementVisning(
    el: GalleriReferanse & { tittel?: string | null; beskrivelse?: string | null },
    galleri: SentraltGalleriTilstand,
): ElementVisning {
    const id = galleriIdFor(el);
    if (id == null && harDibkReferanse(el)) {
        // Gammel fil-id som ikke finnes i DiBKs API: vises ikke, uten reserve fra AGOL.
        return { tittel: '', beskrivelse: null, imageUrl: '', sentralt: true, status: 'utilgjengelig' };
    }
    if (id == null) {
        return {
            tittel: el.tittel ?? '',
            beskrivelse: el.beskrivelse || null,
            imageUrl: egenBildeUrl(el.filId),
            sentralt: false,
            status: 'ok',
        };
    }
    if (galleri.status === 'laster') {
        return { tittel: '', beskrivelse: null, imageUrl: '', sentralt: true, status: 'laster' };
    }
    const fraDibk = galleri.status === 'ok' ? galleri.oppslag.get(id) : undefined;
    if (!fraDibk) {
        return { tittel: '', beskrivelse: null, imageUrl: '', sentralt: true, status: 'utilgjengelig' };
    }
    return {
        tittel: fraDibk.tittel ?? '',
        beskrivelse: fraDibk.beskrivelse || null,
        imageUrl: fraDibk.bildeUrl ? galleriBildeUrl(id, fraDibk.oppdatert) : '',
        sentralt: true,
        status: 'ok',
    };
}
